import type { CSSProperties } from 'react'
import type { CollapseProps } from 'antd'
import { Collapse } from 'antd'
import { CaretRightOutlined } from '@ant-design/icons'
import { Table } from '@/components'
import { CodeBlock, DocsCodeBlock } from '@/components/CodeBlock'
import { TitleWarpper } from '@/pages/application/components/appDetail/utils'
import { CODE_KEY_PLACEHOLDER } from '../constant/base'
import { IntegratedKeys } from '../components/IntegratedKeys'
import ContentURL from '@/pages/application/components/appDetail/components/ContentURL'
import { useScrollBar } from '@/hooks/useScrollBar.ts'

const url = import.meta.env.VITE_AI_API_BASE_URL

const tableColumns = [
  { title: '名称', dataIndex: 'variableName', key: 'variableName' },
  { title: '类型', dataIndex: 'type', key: 'type' },
  { title: '必填', dataIndex: 'required', key: 'required' },
  {
    title: '备注',
    dataIndex: 'placeholder',
    key: 'placeholder',
    render: (_: any, record: any) => (
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: '20px' }}>
        {record.placeholder}
      </div>
    ),
  },
]

const tableData = [
  {
    variableName: 'robot_id',
    type: 'String',
    required: 'true',
    placeholder: '机器人ID（Agent ID）',
  },
  {
    variableName: 'user',
    type: 'String',
    required: 'true',
    placeholder: '用户信息',
  },
  {
    variableName: 'title',
    type: 'String',
    required: 'true',
    placeholder: '对话标题',
  },
  {
    variableName: 'biz_key',
    type: 'String',
    required: 'false',
    placeholder: '业务标识',
  },
]

const tableConversationData = [
  {
    variableName: 'robot_id',
    type: 'String',
    required: 'true',
    placeholder: '机器人ID（Agent ID）',
  },
  {
    variableName: 'conversation_id',
    type: 'String',
    required: 'true',
    placeholder: '对话ID',
  },
  {
    variableName: 'content',
    type: 'String',
    required: 'true',
    placeholder: '用户输入/提问内容',
  },
  {
    variableName: 'inputs',
    type: 'Object',
    required: 'false',
    placeholder: `业务入参:
    以键值对方式提供用户输入字段，与Flow中的变量对应。Key 为变量名称，Value 是参数值。如果字段类型为 Select，传入的 Value 需为预设选项之一。`,
  },
  {
    variableName: 'response_mode',
    type: 'String',
    required: 'false',
    placeholder: `响应模式,默认值为blocking
    ● blocking 阻塞型，等待执行完毕后返回结果。（请求若流程较长可能会被中断）
    ● streaming 流式返回。基于 SSE（Server-Sent Events）实现流式返回。`,
  },
]

const tableConversationListData = [
  {
    variableName: 'robot_id',
    type: 'String',
    required: 'true',
    placeholder: '机器人ID（Agent ID）',
  },
  {
    variableName: 'biz_key',
    type: 'String',
    required: 'false',
    placeholder: '业务标识',
  },
]

const tableHistoryListData = [
  {
    variableName: 'robot_id',
    type: 'String',
    required: 'true',
    placeholder: '机器人ID（Agent ID）',
  },
  {
    variableName: 'conversation_id',
    type: 'String',
    required: 'true',
    placeholder: '对话ID',
  },
  {
    variableName: 'page_number',
    type: 'integer',
    required: 'false',
    placeholder: '当前页，默认值为1',
  },
  {
    variableName: 'page_size',
    type: 'integer',
    required: 'false',
    placeholder: '返回条数，默认值为20',
  },
]

const tableAssistantData = [
  {
    variableName: 'robot_id',
    type: 'String',
    required: 'true',
    placeholder: '机器人ID（Agent ID）',
  },
  {
    variableName: 'user',
    type: 'String',
    required: 'true',
    placeholder: '用户信息',
  },
  {
    variableName: 'content',
    type: 'String',
    required: 'true',
    placeholder: 'assistant消息内容',
  },
]

const curlTemplate = `curl --location '${url}/v1/oapi/agent/chat/conversation/create'
  --header 'Content-Type: application/json'
  --header 'Access-Key: ${CODE_KEY_PLACEHOLDER.API_KEY}'
  --header 'Workspace-Id: ${CODE_KEY_PLACEHOLDER.WORKSPACE_ID}'
  --data '{
    "robot_id": "f4b5e8a7-98c5-4c31-b082-75d23486421d",
    "user": "maoxing",
    "title": "AI面试",
    "biz_key": "mx1651"
}'`

const responseExample = `{
    "code": 200,
    "success": true,
    "message": "SUCCESS",
    "data": {
        "conversation_id": "1e368df914bd486e9084454f75d96d76",
        "uid": -1000,
        "title": "AI面试",
        "robot_id": "f4b5e8a7-98c5-4c31-b082-75d23486421d",
        "update_time": "2023-12-06T20:07:17.426190"
    },
    "now_time": 1701864435
}`

const curlConversationTemplate = `curl --location '${url}/v1/oapi/agent/chat'
--header 'Content-Type: application/json'
--header 'Access-Key: ${CODE_KEY_PLACEHOLDER.API_KEY}'
--header 'Workspace-Id: ${CODE_KEY_PLACEHOLDER.WORKSPACE_ID}'
--data '{
	"robot_id": "f4b5e8a7-98c5-4c31-b082-75d23486421d",
	"conversation_id": "1e368df914bd486e9084454f75d96d76",
	"content": "帮我出个前端面试题",
	"inputs": {},
    "response_mode":"blocking"
}'
`

const responseConversationBlockingExample = `{
  "code": 200,
  "success": true,
  "message": "SUCCESS",
  "data": {
      "status": "SUCCEEDED",
      "duration_time": 67,
      "message": "运行成功",
      "tasks": [
          {
              "name": "AI面试官(flow)",
              "type": "FUNCTION",
              "tool_type": "flow",
              "timestamp": 1701865347,
              "status": "SUCCEEDED",
              "content": {
                  "input": {
                      "betterAI": {
                          "session": {
                              "agentId": "f4b5e8a7-98c5-4c31-b082-75d23486421d",
                              "conversationId": "1e368df914bd486e9084454f75d96d76",
                              "sessionId": "fcd4cf61becd4f589d6220dede888175",
                              "metaData": null,
                              "agentInput": {}
                          }
                      },
                      "flow_id": "d886c560-3f52-42f1-ae2a-b888991ad2b6",
                      "message": "Application Cache 和 Service Workers 的区别"
                  },
                  "output": \"Application Cache（也被称为 AppCache）和 Service Workers 是两种允许网站在用户的设备上缓存资源的技术，它们的目标是提高网站在离线时的可用性，但它们在设计和功能上有着根本的区别。\\n\\n1. 生命周期和控制力：\\n   - **Application Cache** 有一个简单的缓存清单文件（manifest），它告诉浏览器哪些文件需要被缓存。然而，开发者对缓存的控制非常有限，当清单文件更新后，整个应用会重新下载所有文件，而不管它们是否发生了变化。\\n   - **Service Workers** 提供了更细粒度的控制，允许开发者编写脚本来精确控制缓存的内容和缓存的方式。开发者可以拦截网络请求并根据需要提供不同的响应，包括从缓存中提供文件、从网络获取文件或者甚至是生成响应。\\n\\n2. 更新机制：\\n   - 使用 **Application Cache**，浏览器会检查清单文件是否变化，如果有变化，就会重新下载清单文件中列出的全部资源。\\n   - **Service Workers** 允许开发者通过编程决定何时更新缓存中的资源，这可以基于资源的版本，或者其他任何逻辑。\\n\\n3. 浏览器支持和未来：\\n   - **Application Cache** 已经被认为是一种过时的技术，并且在 HTML5 规范中被标记为废弃。现代浏览器已经开始移除对其的支持。\\n   - **Service Workers** 被视为 Application Cache 的替代方案，并且是构建渐进式Web应用（PWA）的关键技术之一，得到了广泛的浏览器支持。\\n\\n4. 功能和用例：\\n   - **Application Cache** 主要用于简单的离线体验，例如静态网站。\\n   - **Service Workers** 支持更复杂的离线方案，包括背景同步、推送通知等。\\n\\n总的来说，Service Workers 提供了比 Application Cache 更强大、更灵活的功能来构建离线体验和渐进式Web应用。\"
              },
              "duration_time": 55,
              "message": "运行成功",
              "metadata": {
                  "color": "#EDD3BB",
                  "icon": "🌵"
              }
          },
          {
              "name": "llm_response",
              "type": "TEXT",
              "tool_type": "SYSTEM",
              "timestamp": 1701865407,
              "status": "SUCCEEDED",
              "content": "请解释同源策略（Same-Origin Policy）是什么，以及它如何影响前端开发。",
              "duration_time": 0.0,
              "message": "运行成功"
          }
      ],
      "timestamp": 1701865340
  },
  "now_time": 1701865407
}`

const responseConversationStreamingExample = `data: {"name": "AI\u9762\u8BD5\u5B98(flow)", "type": "FUNCTION", "tool_type": "flow", "timestamp": 1701865615, "status": "STARTED", "content": {"input": {}}, "duration_time": 0.0, "message": "\u8FD0\u884C\u6210\u529F", "metadata": {"color": "#EDD3BB", "icon": "\uD83C\uDF35"}}
data: {"name": "AI\u9762\u8BD5\u5B98(flow)", "type": "FUNCTION", "tool_type": "flow", "timestamp": 1701865626, "status": "SUCCEEDED", "content": {"input": {"betterAI": {"session": {"agentId": "f4b5e8a7-98c5-4c31-b082-75d23486421d", "conversationId": "1e368df914bd486e9084454f75d96d76", "sessionId": "fcd4cf61becd4f589d6220dede888175", "metaData": null, "agentInput": {}}}, "flow_id": "d886c560-3f52-42f1-ae2a-b888991ad2b6", "message": "\u8BF7\u89E3\u91CA\u540C\u6E90\u7B56\u7565\uFF08Same-Origin Policy\uFF09\u662F\u4EC0\u4E48\uFF0C\u4EE5\u53CA\u5B83\u5982\u4F55\u5F71\u54CD\u524D\u7AEF\u5F00\u53D1\u3002"}, "output": "\"\u540C\u6E90\u7B56\u7565\u662F\u4E00\u79CD\u5B89\u5168\u534F\u8BAE\uFF0C\u5B83\u89C4\u5B9A\u4E86\u4E00\u4E2A\u7F51\u9875\u4E0A\u7684\u811A\u672C\u53EA\u80FD\u8BBF\u95EE\u548C\u64CD\u4F5C\u4E0E\u8BE5\u7F51\u9875\u5177\u6709\u76F8\u540C\u534F\u8BAE\uFF08\u5982\uFF0Chttp \u6216 https\uFF09\u3001\u57DF\u540D\u548C\u7AEF\u53E3\u7684\u8D44\u6E90\u3002\u8FD9\u4E2A\u653F\u7B56\u662F\u4E3A\u4E86\u9632\u6B62\u6076\u610F\u6587\u6863\uFF0C\u4F8B\u5982\u5728\u4E0D\u540C\u57DF\u4E0A\u7684\u7F51\u7AD9\uFF0C\u83B7\u53D6\u654F\u611F\u6570\u636E\u3002\\n\\n\u5728\u524D\u7AEF\u5F00\u53D1\u4E2D\uFF0C\u540C\u6E90\u7B56\u7565\u5BF9\u4E8E\u8FDB\u884C\u8DE8\u57DF\u8D44\u6E90\u5171\u4EAB\uFF08Cross-Origin Resource Sharing, CORS\uFF09\u9020\u6210\u4E86\u9650\u5236\u3002\u4F8B\u5982\uFF0C\u5982\u679C\u4F60\u5C1D\u8BD5\u4ECE\u4E0D\u540C\u7684\u6E90\uFF08\u57DF\u540D\u3001\u534F\u8BAE\u6216\u7AEF\u53E3\uFF09\u4F7F\u7528 AJAX \u8BF7\u6C42\u83B7\u53D6\u8D44\u6E90\uFF0C\u8FD9\u5C06\u4F1A\u88AB\u540C\u6E90\u7B56\u7565\u963B\u6B62\u3002\\n\\n\u8981\u89E3\u51B3\u8FD9\u4E2A\u95EE\u9898\uFF0C\u53EF\u4EE5\u4F7F\u7528\u4E00\u4E9B\u6280\u672F\uFF0C\u6BD4\u5982CORS\u54CD\u5E94\u5934\u3001JSONP\u3001\u6216\u8005\u4F7F\u7528\u4EE3\u7406\u670D\u52A1\u5668\u6765\u7ED5\u8FC7\u8FD9\u4E9B\u9650\u5236\u3002\u8FD9\u4E9B\u65B9\u6CD5\u53EF\u4EE5\u5141\u8BB8\u5F00\u53D1\u8005\u5728\u786E\u4FDD\u5B89\u5168\u7684\u540C\u65F6\uFF0C\u66F4\u7075\u6D3B\u5730\u8FDB\u884C\u8D44\u6E90\u7684\u4EA4\u4E92\u3002\""}, "duration_time": 30, "message": "\u8FD0\u884C\u6210\u529F", "metadata": {"color": "#EDD3BB", "icon": "\uD83C\uDF35"}}
data: {"name": "llm_response", "type": "TEXT", "tool_type": "", "timestamp": 1701865659, "status": "SUCCEEDED", "content": "\u8BF7", "duration_time": 0.0, "message": "\u8FD0\u884C\u6210\u529F"}
data: {"name": "llm_response", "type": "TEXT", "tool_type": "", "timestamp": 1701865659, "status": "SUCCEEDED", "content": "\u89E3", "duration_time": 0.0, "message": "\u8FD0\u884C\u6210\u529F"}
`

const curlConversationListTemplate = `curl --location '${url}/v1/oapi/agent/chat/conversation/list'
--header 'Content-Type: application/json'
--header 'Access-Key: ${CODE_KEY_PLACEHOLDER.API_KEY}'
--header 'Workspace-Id: ${CODE_KEY_PLACEHOLDER.WORKSPACE_ID}'
--data '{
	"robot_id": "28441d30-bf90-4ca5-b0de-6ea03f505cc2",
	"biz_key": "cidjMOsz******AhS4Jg=="
}'`

const responseConversationListExample = `{
  "code": 200,
  "success": true,
  "message": "SUCCESS",
  "data": [
      {
          "conversation_id": "7defe5cebee84cc78fc94b296be320d0",
          "uid": -1000,
          "title": "2023年OKR",
          "robot_id": "28441d30-bf90-4ca5-b0de-6ea03f505cc2",
          "robot_name": "OKR小助手",
          "robot_avatar": "",
          "update_time": "2023-12-28T13:02:15.675865",
          "latest_create_time": "2023-12-28T13:09:11.090987",
          "run_type": "AGENT_OAPI",
          "robot_color": "#F3F1C7",
          "robot_icon": "https://resource.bantouyan.com/betteryeah/agent/avatars/avatar-22.png",
          "robot_welcome_message": ""
      }
  ],
  "now_time": 1704443457
}`

const curlHistoryListTemplate = `curl --location '${url}/v1/oapi/agent/chat/history?robot_id=XXXXXXX&conversation_id=XXXXXXXX'
--header 'Access-Key: ${CODE_KEY_PLACEHOLDER.API_KEY}'
--header 'Workspace-Id: ${CODE_KEY_PLACEHOLDER.WORKSPACE_ID}'
--data ''`

const responseHistoryListExample = `{
  "code": 200,
  "success": true,
  "message": "SUCCESS",
  "data": {
      "page_number": 1,
      "page_size": 20,
      "total_pages": 1,
      "total_count": 3,
      "data_list": [
          {
              "id": "aaf6717bd7d047dabbb55c151d9290b8",
              "user_content": "帮我推荐杭州冬天可以滑雪的地方",
              "response": "请描述一下 Application Cache 和 Service Workers 的区别，并解释为什么后者通常被认为是更好的选择。",
              "file_name": "",
              "session_id": "fcd4cf61becd4f589d6220dede888175",
              "file_type": "",
              "file_url": "",
              "create_time": "2023-12-06T20:20:42.452584",
              "response_ext": {
                  "status": "SUCCEEDED",
                  "duration_time": 43,
                  "message": "运行成功",
                  "tasks": [
                      {
                          "name": "AI面试官(flow)",
                          "type": "FUNCTION",
                          "tool_type": "flow",
                          "timestamp": 1701865252,
                          "status": "SUCCEEDED",
                          "content": {
                              "input": {
                                  "betterAI": {
                                      "session": {
                                          "agentId": "f4b5e8a7-98c5-4c31-b082-75d23486421d",
                                          "conversationId": "1e368df914bd486e9084454f75d96d76",
                                          "sessionId": "fcd4cf61becd4f589d6220dede888175",
                                          "metaData": null,
                                          "agentInput": {}
                                      }
                                  },
                                  "flow_id": "d886c560-3f52-42f1-ae2a-b888991ad2b6",
                                  "message": "请你说说你对HTML5中的缓存API有什么了解？"
                              },
                              "output": \"HTML5 引入了一个新的缓存机制，即 Application Cache（或称为 AppCache），它允许开发人员指定哪些文件应该被浏览器缓存以及在没有网络连接时仍然可以访问。你可以通过创建一个清单文件（manifest file），在里面列出需要被缓存的资源，然后在 HTML 的 \`<html>\` 标签上通过 \`manifest\` 属性引用它。\\n\\n不过，值得注意的是，尽管 Application Cache 提供了使应用可以离线工作的能力，但它已被 Service Workers API 所取代。Service Workers 提供了更强大的控制能力，允许拦截网络请求，并根据需要动态地管理缓存。这使得开发者能够实现复杂的缓存策略，从而创建更有效的离线体验。\\n\\n除此之外，HTML5 还提供了 Web Storage API，包括 localStorage 和 sessionStorage，它们也可以用于在客户端存储数据，但与 Application Cache 不同，Web Storage 用于存储键值对数据，而不是缓存文件。\"
                          },
                          "duration_time": 26,
                          "message": "运行成功",
                          "metadata": {
                              "color": "#EDD3BB",
                              "icon": "🌵"
                          }
                      },
                      {
                          "name": "llm_response",
                          "type": "TEXT",
                          "tool_type": "SYSTEM",
                          "timestamp": 1701865286,
                          "status": "SUCCEEDED",
                          "content": "请描述一下 Application Cache 和 Service Workers 的区别，并解释为什么后者通常被认为是更好的选择。",
                          "duration_time": 0.0,
                          "message": "运行成功"
                      }
                  ],
                  "timestamp": 1701865242
              },
              "record_status": "SUCCEEDED",
              "is_push": false,
              "upload_file_id": ""
          }
      ]
  },
  "now_time": 1701866415
}`

const curlAssistantTemplate = `curl --location '${url}/v1/oapi/agent/chat/push_msg'
--header 'Content-Type: application/json'
--header 'Access-Key: ${CODE_KEY_PLACEHOLDER.API_KEY}'
--header 'Workspace-Id: ${CODE_KEY_PLACEHOLDER.WORKSPACE_ID}'
--data '{
	"robot_id": "7e44701c-62b7-4793-a683-93bc4e02c0cf",
	"conversation_id": "fe5a8ca37eb84e3b819cd3cdcfcda549",
	"content": "xxx任务已处理完成，请继续。"
}'`

const responseAssistantExample = `{
  "code": 200,
  "success": true,
  "message": "SUCCESS",
  "data": null,
  "now_time": 1701912258
}`

export default () => {
  const panelStyle: React.CSSProperties = {
    marginBottom: 20,
    background: '#fff',
    borderRadius: 8,
    border: 'none',
    width: '100%',
  }

  const getItems: (
    panelStyle: CSSProperties,
  ) => CollapseProps['items'] = panelStyle => [
    {
      key: '1',
      label: (
        <div className='text-16px font-500 line-height-25px'>创建对话</div>
      ),
      children: (
        <div className='100%'>
          <div className='mb-24px c-font_1'>
            可用于大部分场景的对话型应用，采用一问一答模式与用户持续对话。要开始一个新对话请调用此接口。
          </div>
          <TitleWarpper text='请求链接' fontSize={14} />
          <ContentURL
            url={`${url}/v1/oapi/agent/chat/conversation/create`}
            method='POST'
            className='adapt:mb-24'
          />
          <TitleWarpper text='请求体' fontSize={14} />
          <Table
            dataSource={tableData}
            columns={tableColumns}
            className='m-b-24px'
            pagination={false}
          />
          <TitleWarpper text='请求示例' fontSize={14} />
          <div className='mb-24px'>
            <DocsCodeBlock str={curlTemplate} lang='bash' />
          </div>
          <TitleWarpper text='响应示例' fontSize={14} />
          <div>
            <CodeBlock str={responseExample} lang='json' />
          </div>
        </div>
      ),
      style: panelStyle,
    },
    {
      key: '2',
      label: (
        <div className='text-16px font-500 line-height-25px'>发送会话消息</div>
      ),
      children: (
        <div className='100%'>
          <TitleWarpper text='请求链接' fontSize={14} />
          <ContentURL
            url={`${url}/v1/oapi/agent/chat`}
            method='POST'
            className='adapt:mb-24'
          />
          <TitleWarpper text='请求体' fontSize={14} />
          <Table
            dataSource={tableConversationData}
            columns={tableColumns}
            className='m-b-24px'
            pagination={false}
          />
          <TitleWarpper text='请求示例' fontSize={14} />
          <div className='mb-24px'>
            <DocsCodeBlock str={curlConversationTemplate} lang='bash' />
          </div>
          <TitleWarpper text='响应示例（blocking）' fontSize={14} />
          <div className='mb-24px'>
            <CodeBlock
              str={responseConversationBlockingExample}
              lang='json'
              className='max-h-400px'
            />
          </div>
          <TitleWarpper text='响应示例（streaming）' fontSize={14} />
          <div>
            <CodeBlock str={responseConversationStreamingExample} lang='json' />
          </div>
        </div>
      ),
      style: panelStyle,
    },
    {
      key: '3',
      label: (
        <div className='text-16px font-500 line-height-25px'>获取会话列表</div>
      ),
      children: (
        <div className='100%'>
          <TitleWarpper text='请求链接' fontSize={14} />
          <ContentURL
            url={`${url}/v1/oapi/agent/chat/conversation/list`}
            method='POST'
            className='adapt:mb-24'
          />
          <TitleWarpper text='请求体' fontSize={14} />
          <Table
            dataSource={tableConversationListData}
            columns={tableColumns}
            className='m-b-24px'
            pagination={false}
          />
          <TitleWarpper text='请求示例' fontSize={14} />
          <div className='mb-24px'>
            <DocsCodeBlock str={curlConversationListTemplate} lang='bash' />
          </div>
          <TitleWarpper text='响应示例' fontSize={14} />
          <div>
            <CodeBlock str={responseConversationListExample} lang='json' />
          </div>
        </div>
      ),
      style: panelStyle,
    },
    {
      key: '4',
      label: (
        <div className='text-16px font-500 line-height-25px'>
          获取对话历史消息
        </div>
      ),
      children: (
        <div className='100%'>
          <TitleWarpper text='请求链接' fontSize={14} />
          <ContentURL
            url={`${url}/v1/oapi/agent/chat/history?robot_id=XXXXXXX&conversation_id=XXXXXXXX`}
            method='GET'
            className='adapt:mb-24'
          />
          <TitleWarpper text='请求体' fontSize={14} />
          <Table
            dataSource={tableHistoryListData}
            columns={tableColumns}
            className='m-b-24px'
            pagination={false}
          />
          <TitleWarpper text='请求示例' fontSize={14} />
          <div className='mb-24px'>
            <DocsCodeBlock str={curlHistoryListTemplate} lang='bash' />
          </div>
          <TitleWarpper text='响应示例' fontSize={14} />
          <div>
            <CodeBlock
              str={responseHistoryListExample}
              lang='json'
              className='max-h-400px'
            />
          </div>
        </div>
      ),
      style: panelStyle,
    },
    {
      key: '5',
      label: (
        <div className='text-16px font-500 line-height-25px'>
          推送assistant消息
        </div>
      ),
      children: (
        <div className='100%'>
          <div className='mb-24px c-font_1'>
            推送assistant消息到对话框主要用于提醒用户xxx任务已完成可以继续对话。
          </div>
          <TitleWarpper text='请求链接' fontSize={14} />
          <ContentURL
            url={`${url}/v1/oapi/agent/chat/push_msg`}
            method='POST'
            className='adapt:mb-24'
          />
          <TitleWarpper text='请求体' fontSize={14} />
          <Table
            dataSource={tableAssistantData}
            columns={tableColumns}
            className='m-b-24px'
            pagination={false}
          />
          <TitleWarpper text='请求示例' fontSize={14} />
          <div className='mb-24px'>
            <DocsCodeBlock str={curlAssistantTemplate} lang='bash' />
          </div>
          <TitleWarpper text='响应示例' fontSize={14} />
          <div>
            <CodeBlock str={responseAssistantExample} lang='json' />
          </div>
        </div>
      ),
      style: panelStyle,
    },
  ]

  const { scrollRef } = useScrollBar()

  return (
    <div
      ref={scrollRef}
      style={{ flex: '0 0 calc(100vw-520px)', width: 'calc(100vw-520px)' }}
      className='p-24px rounded-12px h-full overflow-y-auto'
    >
      <div className='adapt:mb-24 font-500 text-20px line-height-32px'>
        API访问
      </div>
      <IntegratedKeys className='p-24px rounded-8px bg-white mb-20px' />
      <Collapse
        size='large'
        bordered={false}
        defaultActiveKey={['1', '2', '3', '4', '5']}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        style={{ background: 'none', width: '100%' }}
        items={getItems(panelStyle)}
      />
    </div>
  )
}
